/**
 * NOTE:
 * - 운영 환경에서는 API proxy가 `x-missionfund-org` 헤더를 서버로 전달할 때 주입합니다.
 * - 프론트에서는 보안상 해당 헤더를 직접 보내지 않습니다.
 *
 * Local quick test:
 * - env 없이 바로 테스트하려면 아래 DEFAULT를 서버 proxy 주소로 두면 됩니다.
 * - 필요하면 `/Prod` 유무만 서버 proxy 설정에 맞게 조정하세요.
 */
import { refreshAccessToken } from './authApiClient';
import { getAccessToken } from './tokenStorage';

const DEFAULT_API_BASE = "https://sapi.missionfund.org";
const PROJECT_API_BASE = (process.env.REACT_APP_API_BASE || DEFAULT_API_BASE).replace(/\/$/, "");

export class ProjectApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

type HttpMethod = "POST";

const defaultHeaders: Record<string, string> = {
  "Content-Type": "application/json",
};

export async function projectRequest<TResponse>(
  path: string,
  options: { method?: HttpMethod; body?: unknown } = {}
): Promise<TResponse> {
  const { method = "POST", body } = options;
  const request = async (token?: string) => {
    const headers: Record<string, string> = {
      ...defaultHeaders,
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return fetch(`${PROJECT_API_BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    });
  };

  let response = await request(getAccessToken());
  if (response.status === 401) {
    try {
      const nextToken = await refreshAccessToken();
      response = await request(nextToken);
    } catch {
      // keep original unauthorized response handling
    }
  }

  const responseBody = await response.json().catch(() => undefined);

  if (!response.ok) {
    const message =
      (responseBody as { message?: string })?.message ??
      `Project API error ${response.status} on ${path}`;
    throw new ProjectApiError(message, response.status, responseBody);
  }

  return responseBody as TResponse;
}
